/**
 * Game Activity Storage Utility
 *
 * The recent-game-click marker: which game the user just left for, so the app
 * can offer a score form when they come back.
 *
 * This is the one piece of state that deliberately stayed in localStorage
 * under D22. It's genuinely transient (a ten-minute window), device-scoped
 * (it's about *this* tab's navigation, not the account), and worthless to
 * persist - there'd be nothing to show a second device. Everything else that
 * used to live here - games played today, games played ever, the last visited
 * date - is now derived from `plays` server-side.
 */

const STORAGE_KEYS = {
  LAST_LINK_CLICKED: 'lastLinkClicked',
  LAST_LINK_CLICKED_TIME: 'lastLinkClickedTime',
  LAST_LINK_CLICKED_URL: 'lastLinkClickedUrl'
};

/**
 * Safe localStorage operations with error handling
 */
const storage = {
  getString(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn(`Error reading string from localStorage key "${key}":`, error);
      return null;
    }
  },

  setString(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn(`Error writing string to localStorage key "${key}":`, error);
      return false;
    }
  },

  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
      return false;
    }
  }
};

/**
 * Game activity storage operations
 */
export const gameActivityStorage = {
  // Recent game click operations
  getRecentGameClick() {
    const gameId = storage.getString(STORAGE_KEYS.LAST_LINK_CLICKED);
    const clickTime = storage.getString(STORAGE_KEYS.LAST_LINK_CLICKED_TIME);
    const gameUrl = storage.getString(STORAGE_KEYS.LAST_LINK_CLICKED_URL);

    if (!gameId || !clickTime) {
      return null;
    }

    return {
      gameId,
      gameUrl,
      clickTime: parseInt(clickTime)
    };
  },

  setRecentGameClick(gameId, gameUrl, clickTime = Date.now()) {
    const success = [
      storage.setString(STORAGE_KEYS.LAST_LINK_CLICKED, gameId.toString()),
      storage.setString(STORAGE_KEYS.LAST_LINK_CLICKED_TIME, clickTime.toString()),
      storage.setString(STORAGE_KEYS.LAST_LINK_CLICKED_URL, gameUrl)
    ].every(Boolean);

    return success;
  },

  clearRecentGameClick() {
    const success = [
      storage.remove(STORAGE_KEYS.LAST_LINK_CLICKED),
      storage.remove(STORAGE_KEYS.LAST_LINK_CLICKED_TIME),
      storage.remove(STORAGE_KEYS.LAST_LINK_CLICKED_URL)
    ].every(Boolean);

    return success;
  }
};

export default gameActivityStorage;
